import { Injectable } from '@angular/core';
import { ChartFacade } from '@mode/charts/contract';
import { FeatureFlag, FeatureFlagsFacade, FileExportDomain } from '@mode/shared/contract-common';
import { FlamingoFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, switchMap, withLatestFrom } from 'rxjs';
import * as FileExportActions from './file-export/file-export.actions';
import * as FileExportSelectors from './file-export/file-export.selectors';

@Injectable()
export class ChartExportFacade {
  private drawerIsOpenNotifier = new BehaviorSubject<boolean>(false);
  private drawerIsExpandedNotifier = new BehaviorSubject<boolean>(true);

  drawerIsOpen$!: Observable<boolean>;
  drawerIsExpanded$!: Observable<boolean>;
  allExports$: Observable<ReadonlyArray<FileExportDomain.FileExport>>;
  allExportCount$: Observable<number>;
  hasNoExports$: Observable<boolean>;

  constructor(
    private store: Store,
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private featureFlagFacade: FeatureFlagsFacade
  ) {
    this.drawerIsOpen$ = this.drawerIsOpenNotifier.pipe(distinctUntilChanged());
    this.drawerIsExpanded$ = this.drawerIsExpandedNotifier.pipe(distinctUntilChanged());
    this.allExports$ = this.store.select(FileExportSelectors.selectAll);
    this.allExportCount$ = this.store.select(FileExportSelectors.selectTotal);
    this.hasNoExports$ = this.allExportCount$.pipe(map((count) => count <= 0));
  }

  exportDesignerChartCsv(token: string, refId: string) {
    this.store.dispatch(FileExportActions.chartDesignerExportCsv({ token, refId }));
  }

  exportReportChartCsv(token: string) {
    this.store.dispatch(FileExportActions.chartReportExportCsv({ token }));
  }

  exportViewDataCsv() {
    this.store.dispatch(FileExportActions.viewDataChartExportCsv());
    this.openDrawer();
  }

  createLatestExport(token: string) {
    return this.store.select(FileExportSelectors.createLatestChartExportSelector(token));
  }

  createIsChartExportable(vizToken: string, refId: string) {
    return combineLatest([
      this.flamingoFacade
        .createPlanIdFromRefId(refId)
        .pipe(switchMap((planId) => this.flamingoFacade.createReadyFromPlanId(planId))),
      this.chartFacade.createIsValidFromVizToken(vizToken),
      this.chartFacade.createIsCompleteFromVizToken(vizToken),
    ]).pipe(map(([ready, isValid, isComplete]) => ready && isValid && isComplete));
  }

  createIsTableOverLimit(refId: string) {
    return this.createCount(refId).pipe(
      withLatestFrom(this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit)),
      map(([count, limit]) => count != null && count > limit)
    );
  }

  removeExport(planId: string) {
    this.store.dispatch(FileExportActions.deleteExport({ id: planId }));
  }

  retryExport(planId: string) {
    this.store.dispatch(FileExportActions.retryExport({ id: planId }));
  }

  downloadChartDesignerCsv(planId: string) {
    this.store.dispatch(FileExportActions.downloadExport({ id: planId }));
  }

  updateDrawerState(open: boolean) {
    this.drawerIsOpenNotifier.next(open);
  }

  updateDrawerExpanded(expanded: boolean) {
    this.drawerIsExpandedNotifier.next(expanded);
  }

  openDrawer() {
    this.drawerIsOpenNotifier.next(true);
    this.drawerIsExpandedNotifier.next(true);
  }

  private createCount(refId: string) {
    return this.flamingoFacade
      .createPlanIdFromRefId(refId)
      .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)));
  }
}
